import type { ImaController } from "../types";
import Hls from "hls.js";

interface VastMediaFile {
  url: string;
  type: string;
  width: number;
  height: number;
  bitrate?: number | undefined;
}

interface VastTrackingUrls {
  impression: string[];
  start: string[];
  firstQuartile: string[];
  midpoint: string[];
  thirdQuartile: string[];
  complete: string[];
  mute: string[];
  unmute: string[];
  pause: string[];
  resume: string[];
  fullscreen: string[];
  exitFullscreen: string[];
  skip: string[];
  error: string[];
}

interface VastAd {
  id: string;
  title: string;
  duration: number;
  mediaFiles: VastMediaFile[];
  trackingUrls: VastTrackingUrls;
  clickThrough?: string | undefined;
}

export function createHlsAdPlayer(
  contentVideo: HTMLVideoElement,
  options?: {
    continueLiveStreamDuringAds?: boolean;
    licenseKey?: string;
    mainHlsInstance?: Hls;
  }
): ImaController {
  let adPlaying = false;
  let originalMutedState = false;
  let originalVolume = Math.max(0, Math.min(1, contentVideo.volume || 1));
  const listeners = new Map<string, Set<(payload?: any) => void>>();
  const licenseKey = options?.licenseKey;
  const mainHlsInstance = options?.mainHlsInstance;

  let adVideoElement: HTMLVideoElement | undefined;
  let adHls: Hls | undefined;
  let adContainerEl: HTMLDivElement | undefined;
  let currentAd: VastAd | undefined;
  let sessionId: string | undefined;
  const preloadedAds = new Map<string, VastAd>();
  const preloadingAds = new Map<string, Promise<void>>();

  let trackingFired = {
    impression: false,
    start: false,
    firstQuartile: false,
    midpoint: false,
    thirdQuartile: false,
    complete: false,
  };

  function emit(event: string, payload?: any): void {
    const set = listeners.get(event);
    if (!set) return;
    for (const fn of Array.from(set)) {
      try {
        fn(payload);
      } catch (error) {
        console.warn(
          `[HlsAdPlayer] Error in event listener for ${event}:`,
          error
        );
      }
    }
  }

  function generateSessionId(): string {
    return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  function fireTrackingPixels(urls: string[]): void {
    if (!urls || urls.length === 0) return;

    urls.forEach((url) => {
      try {
        let trackingUrl = url;

        if (sessionId) {
          trackingUrl = `${trackingUrl}${
            trackingUrl.includes("?") ? "&" : "?"
          }session_id=${sessionId}`;
        }

        if (licenseKey) {
          trackingUrl = `${trackingUrl}${
            trackingUrl.includes("?") ? "&" : "?"
          }license_key=${licenseKey}`;
        }

        const img = new Image(1, 1);
        img.src = trackingUrl;
        console.log(`[HlsAdPlayer] Fired tracking pixel: ${trackingUrl}`);
      } catch (error) {
        console.warn(`[HlsAdPlayer] Error firing tracking pixel:`, error);
      }
    });
  }

  function getMainStreamQuality(): {
    width: number;
    height: number;
    bitrate: number;
  } | null {
    if (!mainHlsInstance || !mainHlsInstance.levels) {
      return null;
    }

    const currentLevel = mainHlsInstance.currentLevel;
    if (currentLevel === -1 || !mainHlsInstance.levels[currentLevel]) {
      const autoLevel = mainHlsInstance.loadLevel;
      if (autoLevel !== -1 && mainHlsInstance.levels[autoLevel]) {
        const level = mainHlsInstance.levels[autoLevel];
        return {
          width: level.width || 1920,
          height: level.height || 1080,
          bitrate: level.bitrate || 5000000,
        };
      }
      return null;
    }

    const level = mainHlsInstance.levels[currentLevel];
    return {
      width: level.width || 1920,
      height: level.height || 1080,
      bitrate: level.bitrate || 5000000,
    };
  }

  function selectBestMediaFile(mediaFiles: VastMediaFile[]): VastMediaFile {
    if (mediaFiles.length === 0) {
      throw new Error("No media files available");
    }

    const firstFile = mediaFiles[0];
    if (!firstFile) {
      throw new Error("No media files available");
    }

    if (mediaFiles.length === 1) {
      return firstFile;
    }

    const mainQuality = getMainStreamQuality();
    if (!mainQuality) {
      console.log(
        "[HlsAdPlayer] No main stream quality info, using first media file"
      );
      return firstFile;
    }

    console.log("[HlsAdPlayer] Main stream quality:", mainQuality);

    const scoredFiles = mediaFiles.map((file) => {
      const widthDiff = Math.abs(file.width - mainQuality.width);
      const heightDiff = Math.abs(file.height - mainQuality.height);
      const resolutionDiff = widthDiff + heightDiff;

      const fileBitrate = (file.bitrate || 5000) * 1000;
      const bitrateDiff = Math.abs(fileBitrate - mainQuality.bitrate);

      const score = resolutionDiff * 2 + bitrateDiff / 1000;

      return { file, score, resolutionDiff, bitrateDiff };
    });

    scoredFiles.sort((a, b) => a.score - b.score);

    const bestMatch = scoredFiles[0];
    if (!bestMatch) {
      console.log("[HlsAdPlayer] No best match found, using first media file");
      return firstFile;
    }

    console.log("[HlsAdPlayer] Selected media file:", {
      url: bestMatch.file.url,
      resolution: `${bestMatch.file.width}x${bestMatch.file.height}`,
      bitrate: bestMatch.file.bitrate,
      score: bestMatch.score,
      resolutionDiff: bestMatch.resolutionDiff,
      bitrateDiff: bestMatch.bitrateDiff,
    });

    return bestMatch.file;
  }

  function parseVastXml(xmlString: string): VastAd | null {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlString, "text/xml");

      const parserError = xmlDoc.querySelector("parsererror");
      if (parserError) {
        console.error(
          "[HlsAdPlayer] XML parsing error (malformed VAST XML):",
          parserError.textContent
        );
        return null;
      }

      const adElement = xmlDoc.querySelector("Ad");
      if (!adElement) {
        console.warn("[HlsAdPlayer] No Ad element found in VAST XML");
        return null;
      }

      const adId = adElement.getAttribute("id") || "unknown";
      const title = xmlDoc.querySelector("AdTitle")?.textContent || "Ad";

      const isNoAdAvailable =
        adId === "empty" ||
        title.toLowerCase().includes("no ad available") ||
        title.toLowerCase() === "no ad available";

      const durationText =
        xmlDoc.querySelector("Duration")?.textContent || "00:00:30";
      const durationParts = durationText.split(":");
      const duration =
        parseInt(durationParts[0] || "0", 10) * 3600 +
        parseInt(durationParts[1] || "0", 10) * 60 +
        parseInt(durationParts[2] || "0", 10);

      const mediaFileElements = xmlDoc.querySelectorAll("MediaFile");
      const mediaFiles: VastMediaFile[] = [];

      console.log(
        `[HlsAdPlayer] Found ${mediaFileElements.length} MediaFile element(s) in VAST XML`
      );

      mediaFileElements.forEach((mf, index) => {
        const type = mf.getAttribute("type") || "";
        const url = mf.textContent?.trim() || "";
        const width = mf.getAttribute("width") || "";
        const height = mf.getAttribute("height") || "";

        console.log(
          `[HlsAdPlayer] MediaFile ${index}: type="${type}", url="${url}", width="${width}", height="${height}"`
        );

        if (type === "application/x-mpegURL" || type.includes("m3u8")) {
          if (!url) {
            console.warn(
              `[HlsAdPlayer] MediaFile ${index} has HLS type but empty URL`
            );
            return;
          }

          const bitrateAttr = mf.getAttribute("bitrate");
          const bitrateValue = bitrateAttr
            ? parseInt(bitrateAttr, 10)
            : undefined;

          mediaFiles.push({
            url,
            type,
            width: parseInt(width || "1920", 10),
            height: parseInt(height || "1080", 10),
            bitrate:
              bitrateValue && bitrateValue > 0 ? bitrateValue : undefined,
          });

          console.log(`[HlsAdPlayer] Added HLS MediaFile: ${url}`);
        } else {
          console.log(
            `[HlsAdPlayer] MediaFile ${index} ignored (type="${type}" is not HLS)`
          );
        }
      });

      if (mediaFiles.length === 0) {
        if (isNoAdAvailable) {
          console.warn(
            "[HlsAdPlayer] No ads available (VAST response indicates no ads)"
          );
        } else {
          console.warn("[HlsAdPlayer] No HLS media files found in VAST XML");
        }
        return null;
      }

      const trackingUrls: VastTrackingUrls = {
        impression: [],
        start: [],
        firstQuartile: [],
        midpoint: [],
        thirdQuartile: [],
        complete: [],
        mute: [],
        unmute: [],
        pause: [],
        resume: [],
        fullscreen: [],
        exitFullscreen: [],
        skip: [],
        error: [],
      };

      xmlDoc.querySelectorAll("Impression").forEach((el) => {
        const url = el.textContent?.trim();
        if (url) trackingUrls.impression.push(url);
      });

      xmlDoc.querySelectorAll("Tracking").forEach((el) => {
        const event = el.getAttribute("event");
        const url = el.textContent?.trim();
        if (event && url) {
          const eventKey = event as keyof VastTrackingUrls;
          if (trackingUrls[eventKey]) {
            trackingUrls[eventKey].push(url);
          }
        }
      });

      const clickThrough = xmlDoc
        .querySelector("ClickThrough")
        ?.textContent?.trim();

      return {
        id: adId,
        title,
        duration,
        mediaFiles,
        trackingUrls,
        clickThrough,
      };
    } catch (error) {
      console.error("[HlsAdPlayer] Error parsing VAST XML:", error);
      return null;
    }
  }

  async function fetchAndParseVastAd(
    vastTagUrl: string
  ): Promise<VastAd | null> {
    const response = await fetch(vastTagUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch VAST: ${response.statusText}`);
    }

    const vastXml = await response.text();
    console.log("[HlsAdPlayer] VAST XML received");
    console.log(
      "[HlsAdPlayer] VAST XML content (first 2000 chars):",
      vastXml.substring(0, 2000)
    );

    return parseVastXml(vastXml);
  }

  function createAdVideoElement(): HTMLVideoElement {
    const video = document.createElement("video");
    video.style.position = "absolute";
    video.style.left = "0";
    video.style.top = "0";
    video.style.width = "100%";
    video.style.height = "100%";
    video.style.objectFit = "contain";
    video.style.backgroundColor = "#000";
    video.playsInline = true;
    video.muted = false;

    video.volume = 1.0;
    console.log(
      `[HlsAdPlayer] Created ad video element with volume ${video.volume}`
    );

    return video;
  }

  function setupAdEventListeners(): void {
    if (!adVideoElement || !currentAd) return;

    adVideoElement.addEventListener("timeupdate", () => {
      if (!currentAd || !adVideoElement) return;

      const progress = adVideoElement.currentTime / currentAd.duration;

      if (progress >= 0.25 && !trackingFired.firstQuartile) {
        trackingFired.firstQuartile = true;
        fireTrackingPixels(currentAd.trackingUrls.firstQuartile);
      }

      if (progress >= 0.5 && !trackingFired.midpoint) {
        trackingFired.midpoint = true;
        fireTrackingPixels(currentAd.trackingUrls.midpoint);
      }

      if (progress >= 0.75 && !trackingFired.thirdQuartile) {
        trackingFired.thirdQuartile = true;
        fireTrackingPixels(currentAd.trackingUrls.thirdQuartile);
      }
    });

    adVideoElement.addEventListener("playing", () => {
      if (!currentAd || trackingFired.start) return;
      trackingFired.start = true;
      fireTrackingPixels(currentAd.trackingUrls.start);
      console.log("[HlsAdPlayer] Ad started playing");
    });

    adVideoElement.addEventListener("ended", () => {
      if (!currentAd || trackingFired.complete) return;
      trackingFired.complete = true;
      fireTrackingPixels(currentAd.trackingUrls.complete);
      console.log("[HlsAdPlayer] Ad completed");

      handleAdComplete();
    });

    adVideoElement.addEventListener("error", (e) => {
      console.error("[HlsAdPlayer] Ad video error:", e);
      if (currentAd) {
        fireTrackingPixels(currentAd.trackingUrls.error);
      }
      handleAdError();
    });

    adVideoElement.addEventListener("volumechange", () => {
      if (!currentAd) return;
      if (adVideoElement!.muted) {
        fireTrackingPixels(currentAd.trackingUrls.mute);
      } else {
        fireTrackingPixels(currentAd.trackingUrls.unmute);
      }
    });

    adVideoElement.addEventListener("pause", () => {
      if (currentAd && !adVideoElement!.ended) {
        fireTrackingPixels(currentAd.trackingUrls.pause);
      }
    });

    adVideoElement.addEventListener("play", () => {
      if (currentAd && adVideoElement!.currentTime > 0) {
        fireTrackingPixels(currentAd.trackingUrls.resume);
      }
    });
  }

  function setAdPlayingFlag(isPlaying: boolean): void {
    if (isPlaying) {
      contentVideo.dataset.stormcloudAdPlaying = "true";
    } else {
      delete contentVideo.dataset.stormcloudAdPlaying;
    }
  }

  function handleAdComplete(): void {
    console.log("[HlsAdPlayer] Handling ad completion");
    adPlaying = false;
    setAdPlayingFlag(false);

    emit("content_resume");

    setTimeout(() => {
      if (!adPlaying && adContainerEl) {
        adContainerEl.style.display = "none";
        adContainerEl.style.pointerEvents = "none";
        console.log("[HlsAdPlayer] Ad container hidden after completion (waiting for next ad)");
      }
    }, 50);
  }

  function handleAdError(): void {
    console.log("[HlsAdPlayer] Handling ad error");
    adPlaying = false;
    setAdPlayingFlag(false);

    const previousMutedState = contentVideo.muted;
    contentVideo.muted = originalMutedState;
    contentVideo.volume = originalMutedState ? 0 : originalVolume;
    console.log(
      `[HlsAdPlayer] Restored mute state: ${previousMutedState} -> ${originalMutedState}`
    );

    if (adContainerEl) {
      adContainerEl.style.display = "none";
      adContainerEl.style.pointerEvents = "none";
    }

    if (!options?.continueLiveStreamDuringAds) {
      if (contentVideo.paused) {
        contentVideo.play().catch(() => {});
      }
    }

    emit("ad_error");
  }

  return {
    initialize() {
      console.log("[HlsAdPlayer] Initializing");

      if (!adContainerEl) {
        const container = document.createElement("div");
        container.style.position = "absolute";
        container.style.left = "0";
        container.style.top = "0";
        container.style.right = "0";
        container.style.bottom = "0";
        container.style.display = "none";
        container.style.alignItems = "center";
        container.style.justifyContent = "center";
        container.style.pointerEvents = "none";
        container.style.zIndex = "10";
        container.style.backgroundColor = "#000";

        contentVideo.parentElement?.appendChild(container);
        adContainerEl = container;
      }
    },

    async requestAds(vastTagUrl: string) {
      console.log("[HlsAdPlayer] Requesting ads:", vastTagUrl);

      if (adPlaying) {
        console.warn(
          "[HlsAdPlayer] Cannot request new ads while an ad is playing"
        );
        return Promise.reject(new Error("Ad already playing"));
      }

      try {
        sessionId = generateSessionId();
        let ad: VastAd | null | undefined;

        if (preloadedAds.has(vastTagUrl)) {
          ad = preloadedAds.get(vastTagUrl);
          preloadedAds.delete(vastTagUrl);
          console.log(
            "[HlsAdPlayer] Using preloaded VAST response:",
            vastTagUrl
          );
        } else {
          ad = await fetchAndParseVastAd(vastTagUrl);
        }

        if (!ad) {
          console.warn("[HlsAdPlayer] No ads available from VAST response");
          emit("ad_error");
          return Promise.resolve();
        }

        currentAd = ad;
        console.log(
          `[HlsAdPlayer] Ad parsed: ${ad.title}, duration: ${ad.duration}s`
        );

        fireTrackingPixels(ad.trackingUrls.impression);
        trackingFired.impression = true;

        return Promise.resolve();
      } catch (error) {
        console.error("[HlsAdPlayer] Error requesting ads:", error);
        emit("ad_error");
        return Promise.reject(error);
      }
    },
    async preloadAds(vastTagUrl: string) {
      if (!vastTagUrl || vastTagUrl.trim() === "") {
        return Promise.resolve();
      }

      if (preloadedAds.has(vastTagUrl)) {
        return Promise.resolve();
      }

      const inflight = preloadingAds.get(vastTagUrl);
      if (inflight) {
        return inflight;
      }

      const preloadPromise = fetchAndParseVastAd(vastTagUrl)
        .then((ad) => {
          if (ad) {
            preloadedAds.set(vastTagUrl, ad);
            console.log(
              "[HlsAdPlayer] Cached VAST response for preloading:",
              vastTagUrl
            );
          }
        })
        .catch((error) => {
          console.warn("[HlsAdPlayer] Failed to preload VAST response:", error);
          preloadedAds.delete(vastTagUrl);
        })
        .finally(() => {
          preloadingAds.delete(vastTagUrl);
        });

      preloadingAds.set(vastTagUrl, preloadPromise);
      return preloadPromise;
    },
    hasPreloadedAd(vastTagUrl: string) {
      return preloadedAds.has(vastTagUrl);
    },

    async play() {
      if (!currentAd) {
        console.warn(
          "[HlsAdPlayer] Cannot play: No ad loaded (no ads available)"
        );
        return Promise.reject(new Error("No ad loaded"));
      }

      console.log("[HlsAdPlayer] Starting ad playback");

      try {
        if (!adVideoElement) {
          adVideoElement = createAdVideoElement();
          adContainerEl?.appendChild(adVideoElement);
          setupAdEventListeners();
        }

        trackingFired = {
          impression: trackingFired.impression,
          start: false,
          firstQuartile: false,
          midpoint: false,
          thirdQuartile: false,
          complete: false,
        };

        // Capture content volume BEFORE muting
        const contentVolume = contentVideo.volume;
        originalVolume = Math.max(
          0,
          Math.min(1, contentVolume || originalVolume)
        );

        if (!options?.continueLiveStreamDuringAds) {
          contentVideo.pause();
          console.log("[HlsAdPlayer] Content paused (VOD mode)");
        } else {
          console.log("[HlsAdPlayer] Content continues (Live mode)");
        }

        console.log("[HlsAdPlayer] FORCE MUTING main video");
        contentVideo.muted = true;
        contentVideo.volume = 0;
        adPlaying = true;
        setAdPlayingFlag(true);

        if (adVideoElement) {
          const adVolume = originalMutedState ? 0 : originalVolume;
          adVideoElement.volume = Math.max(0, Math.min(1, adVolume));
          adVideoElement.muted = false;
          console.log(
            `[HlsAdPlayer] Set ad video volume to ${adVideoElement.volume}, muted: ${adVideoElement.muted}, originalMutedState: ${originalMutedState}, contentVolume: ${contentVolume}`
          );
        }

        if (adContainerEl) {
          adContainerEl.style.display = "flex";
          adContainerEl.style.pointerEvents = "auto";
        }

        emit("content_pause");

        const mediaFile = selectBestMediaFile(currentAd.mediaFiles);
        if (!mediaFile) {
          throw new Error("No media file available for ad");
        }

        console.log(`[HlsAdPlayer] Loading ad from: ${mediaFile.url}`);

        if (Hls.isSupported()) {
          if (adHls) {
            adHls.destroy();
          }

          adHls = new Hls({
            enableWorker: true,
            lowLatencyMode: false,
          });

          adHls.loadSource(mediaFile.url);
          adHls.attachMedia(adVideoElement);

          adHls.on(Hls.Events.MANIFEST_PARSED, () => {
            console.log("[HlsAdPlayer] HLS manifest parsed, starting playback");
            adVideoElement!.play().catch((error) => {
              console.error("[HlsAdPlayer] Error starting ad playback:", error);
              handleAdError();
            });
          });

          adHls.on(Hls.Events.ERROR, (event, data) => {
            console.error("[HlsAdPlayer] HLS error:", data);
            if (data.fatal) {
              handleAdError();
            }
          });
        } else if (
          adVideoElement.canPlayType("application/vnd.apple.mpegurl")
        ) {
          adVideoElement.src = mediaFile.url;
          adVideoElement.play().catch((error) => {
            console.error("[HlsAdPlayer] Error starting ad playback:", error);
            handleAdError();
          });
        } else {
          throw new Error("HLS not supported");
        }

        return Promise.resolve();
      } catch (error) {
        console.error("[HlsAdPlayer] Error playing ad:", error);
        handleAdError();
        return Promise.reject(error);
      }
    },

    async stop() {
      console.log("[HlsAdPlayer] Stopping ad");
      adPlaying = false;
      setAdPlayingFlag(false);

      if (adContainerEl) {
        adContainerEl.style.display = "none";
        adContainerEl.style.pointerEvents = "none";
      }

      if (adHls) {
        adHls.destroy();
        adHls = undefined;
      }

      if (adVideoElement) {
        adVideoElement.pause();
        adVideoElement.src = "";
      }

      currentAd = undefined;
    },

    destroy() {
      console.log("[HlsAdPlayer] Destroying");
      adPlaying = false;
      setAdPlayingFlag(false);
      contentVideo.muted = originalMutedState;
      contentVideo.volume = originalMutedState ? 0 : originalVolume;

      if (adHls) {
        adHls.destroy();
        adHls = undefined;
      }

      if (adVideoElement) {
        adVideoElement.pause();
        adVideoElement.src = "";
        adVideoElement.remove();
        adVideoElement = undefined;
      }

      if (adContainerEl?.parentElement) {
        adContainerEl.parentElement.removeChild(adContainerEl);
      }

      adContainerEl = undefined;
      currentAd = undefined;
      listeners.clear();
      preloadedAds.clear();
      preloadingAds.clear();
    },

    isAdPlaying() {
      return adPlaying;
    },

    resize(width: number, height: number) {
      console.log(`[HlsAdPlayer] Resizing to ${width}x${height}`);

      if (adContainerEl) {
        adContainerEl.style.width = `${width}px`;
        adContainerEl.style.height = `${height}px`;
      }

      if (adVideoElement) {
        adVideoElement.style.width = `${width}px`;
        adVideoElement.style.height = `${height}px`;
      }
    },

    on(event: string, listener: (payload?: any) => void) {
      if (!listeners.has(event)) listeners.set(event, new Set());
      listeners.get(event)!.add(listener);
    },

    off(event: string, listener: (payload?: any) => void) {
      listeners.get(event)?.delete(listener);
    },

    updateOriginalMutedState(muted: boolean, volume?: number) {
      const nextVolume =
        typeof volume === "number" && !Number.isNaN(volume)
          ? Math.max(0, Math.min(1, volume))
          : originalVolume;
      console.log(
        `[HlsAdPlayer] updateOriginalMutedState called: { muted: ${originalMutedState} -> ${muted}, volume: ${originalVolume} -> ${nextVolume} }`
      );
      originalMutedState = muted;
      originalVolume = nextVolume;
    },

    getOriginalMutedState() {
      return originalMutedState;
    },
    getOriginalVolume() {
      return originalVolume;
    },

    setAdVolume(volume: number) {
      if (adVideoElement && adPlaying) {
        adVideoElement.volume = Math.max(0, Math.min(1, volume));
      }
    },

    getAdVolume(): number {
      if (adVideoElement && adPlaying) {
        return adVideoElement.volume;
      }
      return 1;
    },
    showPlaceholder() {
      if (!adContainerEl) {
        const container = document.createElement("div");
        container.style.position = "absolute";
        container.style.left = "0";
        container.style.top = "0";
        container.style.right = "0";
        container.style.bottom = "0";
        container.style.display = "none";
        container.style.alignItems = "center";
        container.style.justifyContent = "center";
        container.style.pointerEvents = "none";
        container.style.zIndex = "10";
        container.style.backgroundColor = "#000";

        contentVideo.parentElement?.appendChild(container);
        adContainerEl = container;
      }

      if (adContainerEl) {
        adContainerEl.style.display = "flex";
        adContainerEl.style.pointerEvents = "auto";
      }
    },
    hidePlaceholder() {
      if (adContainerEl) {
        adContainerEl.style.display = "none";
        adContainerEl.style.pointerEvents = "none";
      }
    },
  };
}
